// The sandbox playback bar's transport button, as data (design **T6**; design doc **D4** / **D12**
// / §3.2). One control does the whole job — submit a run, explain a refusal, play, pause — and it
// is **fixed at 44 × 44 px in every state**, with colour and symbol carrying the difference. A
// control that changes size as its state changes makes the row twitch, and this row is the one the
// player's thumb returns to constantly.
//
// The states are exactly six and this module is the registry of them, so the component switches on
// a table rather than reinventing the rules in JSX:
//
//   needs-running · the selected version has no run, or the document has uncommitted edits
//   blocked       · the working document does not compile
//   busy          · a run is in flight and is not re-submittable
//   playing       · a loaded trace is advancing
//   paused        · a loaded trace is held
//   withheld      · a Beekeeper turn is in flight, and the server refuses the same three controls
//
// **The `blocked` treatment is the load-bearing part.** It renders `aria-disabled` and *never* the
// `disabled` attribute, because a `disabled` element swallows the click — and the click is how the
// player discovers *why* Run is refusing (it raises the compiler's own diagnostics, §3.3.1). A
// control that looks unavailable and explains itself when pressed is strictly better than one that
// looks unavailable and does nothing. The two facts are therefore separate fields here rather than
// one `disabled` boolean the component could collapse by accident, and `activation` says which
// intent an activation carries so no caller has to infer it from the id.
//
// There is deliberately **no stop control** on this bar: returning to the start is the scrub
// thumb's job, and a second control beside the primary one would be two controls for one intent.
// Nothing in this module produces one.
//
// No React, no DOM — every rule is unit-testable on its own.

// --- the speed ladder ---------------------------------------------------------------

/**
 * The offered speed steps. **½× is deliberately absent** (**D14**), superseding `SeekTransport`'s
 * requirement of a half step *for this bar only*: a 10-minute run takes ten minutes at 1× and about
 * 37 seconds at 16×, so the fast steps are what make watching a whole run — and then another, and
 * another — possible inside one editing phase, which is the loop this screen exists to tighten.
 * The slow end is 1× plus the scrub thumb, which resolves to a tick and is finer than any speed
 * step could be. `client-rounds-review`'s transport is untouched and keeps its ½×
 * (`REVIEW_SPEED_STEPS` in `review-model.ts`).
 */
export const SANDBOX_SPEED_STEPS = [1, 4, 16] as const;

export type SandboxSpeed = (typeof SANDBOX_SPEED_STEPS)[number];

/** The default step: ordinary speed, with the fast steps one tap away. */
export const DEFAULT_SANDBOX_SPEED: SandboxSpeed = 1;

/** A step's label. No vulgar-fraction case here — there is no fractional step to spell. */
export function sandboxSpeedLabel(step: number): string {
  return `${step}×`;
}

/**
 * **What the playhead actually runs at**, given what the player chose and what is admitted.
 *
 * The two are kept apart deliberately. Disabling a control does not change a value behind it: a
 * player who selected 16× while replaying and then pressed Run would otherwise leave the loop
 * multiplying at 16× behind a greyed-out button — the failure intact, merely hidden. So the choice
 * persists untouched and the *effective* step is derived from it here.
 *
 * That derivation is also what makes publication restore the preference for free: when the whole
 * ladder is admitted again this returns the chosen step, with no effect to fire and no second state
 * transition to get wrong.
 *
 * Never faster than asked for — an admitted 16× does not promote a chosen 4× — and never outside the
 * admitted set. `admissible` is guaranteed non-empty by {@link admissibleSandboxSpeeds}.
 */
export function effectiveSandboxSpeed(
  chosen: SandboxSpeed,
  admissible: readonly SandboxSpeed[],
): SandboxSpeed {
  if (admissible.includes(chosen)) {
    return chosen;
  }
  const slower = admissible.filter((step) => step < chosen);
  return slower.length === 0
    ? DEFAULT_SANDBOX_SPEED
    : slower.reduce((fastest, step) => (step > fastest ? step : fastest));
}

/**
 * **Which steps a live watch window can sustain**, while a run is still being written.
 *
 * A device run hands the drawing thread only the first `DEFAULT_WATCH_WINDOW_TICKS` of world state;
 * past that there is nothing left to draw until the run publishes. How long that lasts is entirely a
 * function of how fast the playhead eats it, so the fast steps run out first — at 15 Hz the shipped
 * 900-tick window is 60 s at 1×, 15 s at 4× and **3.75 s** at 16×. Measured: at 16× the world view
 * emptied 3.8 s into a run and stayed empty until publication.
 *
 * A step is admitted iff the window lasts at least `minWatchSeconds` at that step:
 *
 * ```
 * windowTicks / (tickRateHz × step) >= minWatchSeconds
 * ```
 *
 * **Derived rather than listed**, so the rule and the window cannot drift apart: at the shipped
 * values this admits 1× alone, and raising the window to 3 600 opens 4× with no second edit. The
 * numbers arrive as arguments precisely so this module — which the screen imports — needs no import
 * of its own from the run driver's side of the app.
 *
 * The result is ascending, duplicate-free, always a subset of {@link SANDBOX_SPEED_STEPS}, and
 * **always contains {@link DEFAULT_SANDBOX_SPEED}**: a ladder with no interactive step is not a state
 * this can produce. That last guarantee is also the fallback for a nonsense window or rate — a zero,
 * negative or non-finite input divides to `NaN`/`Infinity`, which would otherwise admit either
 * nothing or everything, so it degrades to the default step instead.
 */
export function admissibleSandboxSpeeds(
  windowTicks: number,
  tickRateHz: number,
  minWatchSeconds: number,
): readonly SandboxSpeed[] {
  const usable = (value: number): boolean => Number.isFinite(value) && value > 0;
  if (!usable(windowTicks) || !usable(tickRateHz) || !usable(minWatchSeconds)) {
    return [DEFAULT_SANDBOX_SPEED];
  }
  const admitted = SANDBOX_SPEED_STEPS.filter(
    (step) => windowTicks / (tickRateHz * step) >= minWatchSeconds,
  );
  return admitted.includes(DEFAULT_SANDBOX_SPEED) ? admitted : [DEFAULT_SANDBOX_SPEED];
}

// --- the transport control ----------------------------------------------------------

/**
 * The control's box, in px, **in every state** (**D4**). Exported as one constant rather than as a
 * per-state field precisely so no state can carry a different size: the row's geometry must never
 * depend on the transport's state, and only the version selector may flex.
 */
export const TRANSPORT_CONTROL_SIZE_PX = 44;

/** The six states, in the order the spec lists them. */
export const TRANSPORT_STATE_IDS = [
  'needs-running',
  'blocked',
  'busy',
  'playing',
  'paused',
  'withheld',
] as const;

export type TransportStateId = (typeof TRANSPORT_STATE_IDS)[number];

/**
 * What an activation *means*. Naming the intent here keeps the component from switching on the id
 * and guessing — and makes `inert` (a control that receives nothing) a first-class case rather than
 * the absence of a handler.
 */
export type TransportActivation =
  /** Submit a run, committing the working document first if it is dirty (**D12**). */
  | 'submit'
  /** Raise the compiler's diagnostics, with a control to open the first offending location. */
  | 'diagnostics'
  /** Play or pause the loaded trace. */
  | 'toggle-playback'
  /** Nothing to dispatch — the control is showing progress, or a turn owns the document. */
  | 'inert';

/**
 * The control's colour treatment. Colour, not shape or width, is the whole signal.
 * `accent` is the instruction ("this needs running"); `blocked` is a muted fill that reads as
 * unavailable while staying tappable; `neutral` is the resting playback treatment.
 */
export type TransportTone = 'accent' | 'blocked' | 'neutral';

/** Everything the component needs to render one state — and nothing that could vary its geometry. */
export interface TransportControlState {
  id: TransportStateId;
  /** The glyph in the box, or `null` where a spinner takes its place. */
  symbol: string | null;
  /** The accessible name / tooltip: what pressing it does, in plain language. */
  label: string;
  activation: TransportActivation;
  tone: TransportTone;
  /**
   * The ARIA state. True wherever the control will not do the thing its symbol implies — including
   * `blocked`, where it is the *only* signal, since the attribute below stays off.
   */
  ariaDisabled: boolean;
  /**
   * The DOM `disabled` attribute. **Never true for `blocked`** (design **T6**): the attribute
   * suppresses the event, and the event is how the player learns the reason. It is also off for
   * `busy`, so a control the player just pressed keeps focus and its progress can be announced;
   * only `withheld` — genuinely unavailable for the duration of someone else's turn — sets it.
   */
  disabledAttribute: boolean;
  /** `aria-busy`: a run is writing, and the progress is on the track (**D10**). */
  busy: boolean;
  /** The small warning pip that marks a draft with script errors. `blocked` only. */
  warningPip: boolean;
}

/** The registry. Every state carries the same field set, so none can grow a private affordance. */
export const TRANSPORT_STATES: Record<TransportStateId, TransportControlState> = {
  'needs-running': {
    id: 'needs-running',
    symbol: '▶',
    // Covers both readings of the state: a version that has never been simulated, and a draft whose
    // edits Run will commit first (**D12**). One sentence for both, because the player's gesture is
    // the same one either way.
    label: 'Run this version',
    activation: 'submit',
    tone: 'accent',
    ariaDisabled: false,
    disabledAttribute: false,
    busy: false,
    warningPip: false,
  },
  blocked: {
    id: 'blocked',
    // Still `▶`, not a warning glyph: the button's job has not changed, only its availability, and
    // the pip beside it is what says something is wrong.
    symbol: '▶',
    label: 'This draft has script errors',
    activation: 'diagnostics',
    tone: 'blocked',
    ariaDisabled: true,
    disabledAttribute: false,
    busy: false,
    warningPip: true,
  },
  busy: {
    id: 'busy',
    symbol: null,
    label: 'Simulating…',
    activation: 'inert',
    tone: 'neutral',
    ariaDisabled: true,
    disabledAttribute: false,
    busy: true,
    warningPip: false,
  },
  playing: {
    id: 'playing',
    symbol: '⏸',
    label: 'Pause',
    activation: 'toggle-playback',
    tone: 'neutral',
    ariaDisabled: false,
    disabledAttribute: false,
    busy: false,
    warningPip: false,
  },
  paused: {
    id: 'paused',
    symbol: '▶',
    label: 'Play',
    activation: 'toggle-playback',
    tone: 'neutral',
    ariaDisabled: false,
    disabledAttribute: false,
    busy: false,
    warningPip: false,
  },
  withheld: {
    id: 'withheld',
    symbol: '⏸',
    label: 'The Beekeeper is rewriting this colony',
    activation: 'inert',
    tone: 'neutral',
    ariaDisabled: true,
    disabledAttribute: true,
    busy: false,
    warningPip: false,
  },
};

/** Everything the state depends on, and nothing else. */
export interface TransportInput {
  /**
   * A Beekeeper turn is in flight. The server refuses Activate, Run and hand edits for its
   * duration, so offering any of the three would be offering a control whose dispatch is
   * guaranteed to be refused — a worse failure than not offering it.
   */
  turnInFlight: boolean;
  /** A batch run is in flight for this sandbox. Note the asymmetry with a *turn*: a run gates only
   * re-submission, not editing. */
  runInFlight: boolean;
  /** The working document compiles — the negation of the server's `document_invalid` refusal, which
   * this state is a pre-echo of rather than a second source of truth. */
  documentCompiles: boolean;
  /** The working document has uncommitted hand edits, so it matches no version. */
  documentDirty: boolean;
  /**
   * The selected version's run has published **and** its trace is resident, so there is something
   * to play. False both where the version has never been run and while its trace is still loading:
   * in either case Run, not Play, is the honest offer.
   */
  selectedRunPlayable: boolean;
  /** Playback is advancing. */
  playing: boolean;
}

/**
 * Document / run / turn state → the transport control's state.
 *
 * The precedence is deliberate. A turn outranks everything because the server refuses the whole
 * mutation surface while one is in flight; a run in flight outranks the document because there is
 * nothing to re-submit until it lands. Below those, the control is a *submit* control wherever
 * there is something to run — and `blocked` is a refusal to run, so it only applies there. A clean
 * document that no longer compiles must not strand a player mid-playback.
 */
export function transportStateFor(input: TransportInput): TransportControlState {
  if (input.turnInFlight) {
    return TRANSPORT_STATES.withheld;
  }
  if (input.runInFlight) {
    return TRANSPORT_STATES.busy;
  }
  const needsRun = input.documentDirty || !input.selectedRunPlayable;
  if (needsRun) {
    return input.documentCompiles ? TRANSPORT_STATES['needs-running'] : TRANSPORT_STATES.blocked;
  }
  return input.playing ? TRANSPORT_STATES.playing : TRANSPORT_STATES.paused;
}
